import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import "./profile.css";
import { SnackbarUI, useSnackbar } from "../header/snackbarProvider";
import { ErrorMessage, Form, Formik } from "formik";
import SelectPicker from "../selectpicker/selectPicker";
import ConfirmationDialog from "../header/confirmation.dialog";
import { EducationalInfoDto, getEducationalInfoById, getUserInfo, submitEducationalInfoAdd, submitEducationalInfoUpdate } from "../../services/profile.service";

const educationInfoValidationSchema = Yup.object({
  graduationMonth: Yup.string().required("Graduation Month is required"),
  graduationYear: Yup.string().required("Graduation Year is required"),
  fieldOfStudy: Yup.string().required("Field of Study is required"),
  degreeAwarded: Yup.string().required("Degree Awarded is required")
});


const fieldOfStudyOptions = [
  { value: "Biological and Medical Sciences", label: "Biological and Medical Sciences" },
  { value: "Computer Sciences and Information Technologies", label: "Computer Sciences and Information Technologies" },
  { value: "Engineering", label: "Engineering" },
  { value: "Technology", label: "Technology" },
  { value: "Science", label: "Science" },
  { value: "Mathematics", label: "Mathematics" },
  { value: "Physical Sciences", label: "Physical Sciences" },
  { value: "Technical Communications, Education, Management, Law and Policy", label: "Technical Communications, Education, Management, Law and Policy" },
  { value: "Other", label: "Other" }
];

const degreeAwardedOptions = [
  { value: "Bachelor of Computer Application(BCA)", label: "Bachelor of Computer Application(BCA)" },
  { value: "Bachelor of Engineering", label: "Bachelor of Engineering" },
  { value: "Bachelor of Technology", label: "Bachelor of Technology" },
  { value: "Diploma Engineering", label: "Diploma Engineering" },
  { value: "Ph.D.", label: "Ph.D." },
  { value: "Master of Science In Information Technology (M.Sc.IT)", label: "Master of Science In Information Technology (M.Sc.IT)" },
  { value: "Master of Computer Application (MCA)", label: "Master of Computer Application (MCA)" },
  { value: "Master of Engineering", label: "Master of Engineering" },
  { value: "Master of Technology", label: "Master of Technology" },
  { value: "MSTR", label: "Master of Science" },
  { value: "Matura (EU,Russia,China,India)", label: "Matura (EU,Russia,China,India)" },
  { value: "MBA", label: "MBA" },
  { value: "Polytechnic Eng.", label: "Polytechnic Eng." },
  { value: "Other", label: "Other" }
];


const graduationMonthOptions = [
  { value: "January", label: "January" },
  { value: "February", label: "February" },
  { value: "March", label: "March" },
  { value: "April", label: "April" },
  { value: "May", label: "May" },
  { value: "June", label: "June" },
  { value: "July", label: "July" },
  { value: "August", label: "August" },
  { value: "September", label: "September" },
  { value: "October", label: "October" },
  { value: "November", label: "November" },
  { value: "December", label: "December" }
];


const generateYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 51 }, (_, i) => {
    const year = currentYear - i; // Reverse order
    return { value: year.toString(), label: year.toString() };
  });
};
const graduationYearOptions = generateYears();


const EducationalInfoComponent = () => {
  
  const { showSnackbar, snackbar } = useSnackbar();
  const [usePersonalInfo, setUserPersonalInfo] = useState(null);
  const [useEducationalInfo, setEducationalInfo] = useState(null);
  const [openEducConfirmationDialog, setOpenEducConfirmationDialog] = useState(false);
  const [dialogValues, setDialogValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);

 //Using explit mode states for preview and update for clarity
 const [isPreviewMode, setIsPreviewMode] = useState(false);
 const [isUpdateMode, setIsUpdateMode] = useState(false);

      
 const handleEducDialogClose = () => {
    setOpenEducConfirmationDialog(false);
    setIsSubmitting(false); // Re-enable submit button
  };

  const [initialValuesOfEducationalInfo, setInitialValuesOfEducationalInfo] = useState({
     graduationMonth: "", 
     graduationYear: "",
     fieldOfStudy: "",
     degreeAwarded: "",
  });
          
            useEffect(() => {
              const fetchData = async () => {
                try {
                  const userInfoDetails = await getUserInfo();
                  if (userInfoDetails && userInfoDetails.length > 0) {
                    const userInfo = userInfoDetails[0];
                    setUserPersonalInfo(userInfo);
          
                    const userEducationalDetails = await getEducationalInfoById(userInfo.loginId);
                    if (userEducationalDetails && Object.keys(userEducationalDetails).length > 0) {
                      setEducationalInfo(userEducationalDetails);
                      setInitialValuesOfEducationalInfo({
                        graduationMonth: userEducationalDetails.graduationMonth ?? "",  
                        graduationYear: userEducationalDetails.graduationYear ?? "",  
                        fieldOfStudy: userEducationalDetails.fieldOfStudy ?? "",  
                        degreeAwarded: userEducationalDetails.degree ?? "",  
                      });
                      // Show preview if data exists
                      setIsPreviewMode(true);
                      setIsUpdateMode(false);
                    } else {
                      // No data exists, show form directly
                      setIsPreviewMode(false);
                      setIsUpdateMode(false);
                    }
                  }
                } catch (error) {
                  console.error("Error fetching educational-info details:", error);
                }
              };
            
              fetchData();
            }, []);


            

  

const handleEducationalInfoSubmit = async (values, { setSubmitting, validateForm }) => {
  const errors = await validateForm();
  if (Object.keys(errors).length > 0) {
    setSubmitting(false);
    return;
  }

  setIsSubmitting(true);
  setOpenEducConfirmationDialog(true);
  setDialogValues(values);
};


const handleEducationalInfoDialogConfirm = async () => {
  setOpenEducConfirmationDialog(false);

  if (isSubmitting) {
    setIsLoading(true);
    setShowLoadingModal(true);

    try {
      let action = useEducationalInfo && Object.keys(useEducationalInfo).length > 0 ? "update" : "add";
  
      const educationalInfoDto = new EducationalInfoDto(
        action === "update" ? useEducationalInfo.educationalInfoId : "",
        usePersonalInfo.loginId,
        dialogValues.degreeAwarded,
        dialogValues.fieldOfStudy,
        dialogValues.graduationMonth,
        dialogValues.graduationYear,
      );

      const result =
        action === "update"
          ? await submitEducationalInfoUpdate(educationalInfoDto)
          : await submitEducationalInfoAdd(educationalInfoDto);

      if (result) {
        const userEducationalDetails = await getEducationalInfoById(usePersonalInfo.loginId);
        if (userEducationalDetails && Object.keys(userEducationalDetails).length > 0) {
          setEducationalInfo(userEducationalDetails);
          setInitialValuesOfEducationalInfo({
            graduationMonth: userEducationalDetails.graduationMonth ?? "",  
            graduationYear: userEducationalDetails.graduationYear ?? "",  
            fieldOfStudy: userEducationalDetails.fieldOfStudy ?? "",  
            degreeAwarded: userEducationalDetails.degree ?? "",  
          });
          // After successful submission, show preview
          setIsPreviewMode(true);
          setIsUpdateMode(false);
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
        showSnackbar(
          action === "add"
            ? "Educational info added successfully!"
            : "Educational info updated successfully!",
          "success"
        );
      } else {

        window.scrollTo({ top: 0, behavior: 'smooth' });
        showSnackbar(
          action === "add"
            ? "Failed to add educational info."
            : "Failed to update educational info.",
          "danger"
        );
      }
    } catch (error) {
      console.error("Error handling educational info:", error);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      showSnackbar("An error occurred while processing educational info.", "danger");
    } finally {
      setIsLoading(false);
      setShowLoadingModal(false);
      setIsSubmitting(false);
    }
  }
};



const handleEditClick = () => {
  // Switch to update mode (form with pre-filled data) if edit button is clicked
  setIsPreviewMode(false);
  setIsUpdateMode(true);
};
  
    return (
        <>
   <div className="profile-master">
    
     {/* Render the Snackbar UI here at the top of the page */}
     {snackbar.open && <SnackbarUI message={snackbar.message} type={snackbar.type} />}


     {isPreviewMode && !isUpdateMode ? (
      <div className="profile-content">
      <div className="profile-info-preview">
        <h4>Academic Qualification Information</h4>
        <div className="preview-content-container">
          <div className="preview-grid">
            <div className="preview-item">
              <span className="preview-label">Degree Awarded:</span>
              <span className="preview-value">
              {useEducationalInfo.degree
                  ? (degreeAwardedOptions.find(
                 (opt) => opt.value === useEducationalInfo.degree
                 )?.label || "")
               : ""}
                </span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Field of Study:</span>
              <span className="preview-value">
              {useEducationalInfo.fieldOfStudy
                  ? (fieldOfStudyOptions.find(
                 (opt) => opt.value === useEducationalInfo.fieldOfStudy
                 )?.label || "")
               : ""}
                </span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Graduation Month:</span>
              <span className="preview-value">
                {useEducationalInfo.graduationMonth} 
              </span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Graduation Year:</span>
              <span className="preview-value">
               {useEducationalInfo.graduationYear}
              </span>
            </div>
          </div>
          <button 
            type="button" 
             className="btn btn-warning mt-4"
            onClick={handleEditClick}
          >
            Edit
          </button>
        </div>
      </div>
    </div>
      ) : (


     <Formik
            initialValues={initialValuesOfEducationalInfo}
            enableReinitialize={true}
            validationSchema={educationInfoValidationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={handleEducationalInfoSubmit}
          >
       
            {({ values, touched, errors, setFieldValue, setFieldTouched }) => (
              <Form style={{ width: "100%", maxWidth: "100%" }}>
                   <div className="profile-content">
                    
                    <div className="row">
            {/* Graduation Month */}
            <div className="col-md-2 mb-3">
            <SelectPicker
                  name="graduationMonth"
                  key={values.graduationMonth} 
                  options={graduationMonthOptions}
                  value={graduationMonthOptions.find((opt) => opt.value === values.graduationMonth) || ""}
                  handleChange={(selectedOption) => setFieldValue("graduationMonth", selectedOption?.value || "")}
                  label="Graduation Month"
                  required={true}
                  error={touched.graduationMonth && errors.graduationMonth ? errors.graduationMonth : ""}
                  touched={touched.graduationMonth}
                  setFieldTouched={setFieldTouched}
                />
                <ErrorMessage name="graduationMonth" component="div" className="invalid-feedback" />
             </div>


              {/* Graduation Year */}
              <div className="col-md-2 mb-3">
              <SelectPicker
                    name="graduationYear"
                     key={values.graduationYear} 
                     options={graduationYearOptions}
                     value={graduationYearOptions.find((opt) => opt.value === values.graduationYear) || ""}
                     handleChange={(selectedOption) => setFieldValue("graduationYear", selectedOption?.value || "")}
                     label="Graduation Year"
                     required={true}
                     error={touched.graduationYear && errors.graduationYear ? errors.graduationYear : ""}
                     touched={touched.graduationYear}
                     setFieldTouched={setFieldTouched}
                  />
                <ErrorMessage name="graduationYear" component="div" className="invalid-feedback" />
              </div>

              {/* Field of Study */}
              <div className="col-md-4 mb-3">
                <SelectPicker
                  name="fieldOfStudy"
                  key={values.fieldOfStudy} 
                  options={fieldOfStudyOptions}
                  value={fieldOfStudyOptions.find((opt) => opt.value === values.fieldOfStudy) || ""}
                  handleChange={(selectedOption) => setFieldValue("fieldOfStudy", selectedOption?.value || "")}
                  label="Undergraduate field of study"
                  required={true}
                  error={touched.fieldOfStudy && errors.fieldOfStudy ? errors.fieldOfStudy : ""}
                  touched={touched.fieldOfStudy}
                  setFieldTouched={setFieldTouched}
               />
                <ErrorMessage name="fieldOfStudy" component="div" className="invalid-feedback" />
              </div>

              {/* Degree Awarded */}
              <div className="col-md-4 mb-3">
                <SelectPicker
                  name="degreeAwarded"
                  key={values.degreeAwarded} 
                  options={degreeAwardedOptions}
                  value={degreeAwardedOptions.find((opt) => opt.value === values.degreeAwarded) || null}
                  handleChange={(selectedOption) => setFieldValue("degreeAwarded", selectedOption?.value || "")}
                  label="Degree Awarded"
                  required={true}
                  error={touched.degreeAwarded && errors.degreeAwarded ? errors.degreeAwarded : ""}
                  touched={touched.degreeAwarded}
                  setFieldTouched={setFieldTouched}
                />
                <ErrorMessage name="degreeAwarded" component="div" className="invalid-feedback" />
              </div>
                   </div>
                 </div>
                 <div className="btn-div">
                 <button type="submit" className={`btn ${isUpdateMode ? "btn edit" : "btn add"}`}>
                  {isUpdateMode ? "Update" : "Submit"}
                </button>

                </div> 

                   </Form>
            )}

          </Formik>

)}


          <ConfirmationDialog
        open={openEducConfirmationDialog}
        onClose={handleEducDialogClose}
        onConfirm={handleEducationalInfoDialogConfirm}
        title=""
        message="Are you sure to submit?"
      />

   </div>

   
   {showLoadingModal && (
  <div className="loading-overlay">
    <div className="loading-container">
      <div className="loading-header">Processing...</div>
      <div className="loading-body">
        <div className="spinner-border text-primary loading-spinner" role="status"></div>
        <p className="mt-3">Please wait...</p>
      </div>
    </div>
  </div>
)}

       </>
  )
}
export default EducationalInfoComponent;

